// components/DrivefolderDropdownPreview.tsx
import React, { useState } from "react";
import { DriveFolder } from "@shared/interfaces/CloudFiles";
import { ServerFile } from "@shared/interfaces/ProjectsFiles";
import { getFileIcon } from "@pages/Project/components/Tabs/FileUploadTab";

const ROW_BASE_CLASSES =
  "px-3 py-2.5 flex items-center cursor-pointer text-gray-900 text-xs 2xl:text-sm hover:bg-[#F2F4F7]";

interface Props {
  folder: DriveFolder;
  projectId: string;
  isSelected: (id: string) => boolean;
  onToggle: (file: ServerFile) => void;
  showOneLine?: boolean;

  /** New generic props */
  provider: "google" | "onedrive";
  iconSrc: string; // e.g. "/icon_google_cloud.svg" or "/icon_onedrive.svg"
  getFolderFiles: (
    projectId: string,
    folderId: string
  ) => Promise<ServerFile[]>;
}

const ArrowIcon: React.FC<{ isOpen: boolean }> = ({ isOpen }) => (
  <img
    src="/arrow-down.svg"
    className={`w-4 h-4 transform transition-transform duration-200 ${
      isOpen ? "rotate-180" : ""
    }`}
    alt=""
  />
);

const DriveFolderDropdownPreview: React.FC<Props> = ({
  folder,
  projectId,
  isSelected,
  onToggle,
  showOneLine = true,
  provider,
  iconSrc,
  getFolderFiles,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [files, setFiles] = useState<ServerFile[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchFolderFiles = async () => {
    setLoading(true);
    try {
      const res = await getFolderFiles(projectId, folder.id);
      setFiles((res ?? []).filter((f: ServerFile) => f.status === "ready"));
    } catch (err) {
      console.error(`Error fetching ${provider} folder files:`, err);
    } finally {
      setLoading(false);
    }
  };

  const toggleOpen = async () => {
    if (!isOpen && files.length === 0) {
      await fetchFolderFiles();
    }
    setIsOpen((prev) => !prev);
  };

  return (
    <>
      <div
        className="px-3 py-2 text-[11px] 2xl:text-xs font-semibold text-gray-500 bg-gray-50 sticky top-0 z-10 flex justify-between items-center cursor-pointer select-none"
        onClick={toggleOpen}
      >
        <div className="flex items-center space-x-1">
          <img src={iconSrc} alt="" className="h-4 w-4" />
          <span>{folder.name}</span>
        </div>
        <ArrowIcon isOpen={isOpen} />
      </div>

      {isOpen && (
        <>
          {loading && (
            <div className="px-3 py-2 text-xs text-gray-400">Loading…</div>
          )}
          {files.map((file) => {
            const checked = isSelected(file.id);
            return (
              <div
                key={file.id}
                onClick={() => onToggle(file)}
                className={`${ROW_BASE_CLASSES} ${
                  checked ? "bg-[#F2F4F7]" : ""
                }`}
              >
                <input
                  type="checkbox"
                  className="mr-2 accent-blue-600"
                  checked={checked}
                  onChange={() => onToggle(file)}
                  onClick={(e) => e.stopPropagation()}
                />
                <img
                  src={getFileIcon(file.filetype)}
                  alt=""
                  className="h-5 w-5 mr-2"
                />
                <p
                  className={
                    showOneLine
                      ? "text-ellipsis line-clamp-1"
                      : "break-all whitespace-normal"
                  }
                >
                  {file.filename}
                </p>
              </div>
            );
          })}
          {!loading && files.length === 0 && (
            <div className="px-3 py-2 text-xs text-gray-400">
              No files in this folder
            </div>
          )}
        </>
      )}
    </>
  );
};

export default DriveFolderDropdownPreview;
